import { useState } from 'react'
import { enregistrerEntretien } from '../../api/candidatures'
import {
  CANEVAS_ACTIF,
  ERREURS_CANEVAS,
  calculerScore,
  criteresEliminatoires,
  questionsSansReponse,
  recommandationAutorisee,
  recommandationSuggeree,
  reponsesVersJson,
} from '../../onboarding/canevas'
import type { Recommandation, Reponses } from '../../onboarding/canevas-types'
import { Erreur, useAction } from './Briques'
import { GrillePapier } from './GrillePapier'

/**
 * Écran d'entretien : déroule la grille section par section, puis la synthèse.
 * Tout est piloté par le canevas (`CANEVAS_ACTIF`) : changer le fichier de
 * contenu change l'écran, sans toucher au code.
 *
 * Le score et les éliminatoires se recalculent à chaque saisie ; la base
 * (contrainte `entretien_elimine_pas_retenu`) refuse de toute façon « retenir »
 * quand un éliminatoire est déclenché.
 */
const LIBELLE_RECO: Record<Recommandation, string> = { retenir: 'Retenir', revoir: 'À revoir', refuser: 'Refuser' }

export function EntretienEcran({
  driverId,
  nom,
  surEnregistre,
}: {
  driverId: string
  nom: string
  surEnregistre: () => void
}) {
  const canevas = CANEVAS_ACTIF
  const [etape, setEtape] = useState(0) // 0..n-1 = sections, n = synthèse
  const [reponses, setReponses] = useState<Reponses>({})
  const [criteres, setCriteres] = useState<Record<string, number>>({})
  const [choix, setChoix] = useState<Recommandation | null>(null)
  const [notes, setNotes] = useState('')
  const [papier, setPapier] = useState(false)
  const { occupe, erreur, lancer, signaler } = useAction()

  if (ERREURS_CANEVAS.length > 0) {
    return (
      <div className="dos-erreur">
        <b>Le canevas d'entretien est invalide</b> (fichier <code>src/onboarding/canevas-entretien.ts</code>) :
        <ul style={{ margin: '6px 0 0 18px' }}>
          {ERREURS_CANEVAS.map((e) => <li key={e}>{e}</li>)}
        </ul>
      </div>
    )
  }

  const n = canevas.sections.length
  const surSynthese = etape >= n
  const section = canevas.sections[etape]
  const score = calculerScore(canevas, reponses)
  const declenchees = criteresEliminatoires(canevas, reponses)
  const elimine = declenchees.length > 0
  const suggeree = recommandationSuggeree(score, elimine)
  const reco: Recommandation = choix && recommandationAutorisee(choix, elimine) ? choix : suggeree

  const repondre = (id: string, v: number | boolean | string) => setReponses((r) => ({ ...r, [id]: v }))

  async function enregistrer() {
    const sans = questionsSansReponse(canevas, reponses)
    if (
      sans.length > 0 &&
      !window.confirm(`${sans.length} question(s) sans réponse. Enregistrer l'entretien quand même ?`)
    ) {
      return
    }
    if (!recommandationAutorisee(reco, elimine)) {
      signaler('Un critère éliminatoire est déclenché : « retenir » est impossible.')
      return
    }
    const ok = await lancer(() =>
      enregistrerEntretien({
        driverId,
        canevasVersion: String(canevas.version),
        reponses: reponsesVersJson(canevas, reponses, { criteres, notes }, reco),
        score,
        elimine,
        recommandation: reco,
        notes,
      }),
    )
    if (ok) surEnregistre()
  }

  return (
    <>
      <div className="ent-nav">
        {canevas.sections.map((s, i) => (
          <button
            key={s.id}
            className={`chip${etape === i ? ' active' : ''}`}
            onClick={() => setEtape(i)}
          >
            {i + 1}. {s.titre}
          </button>
        ))}
        <button className={`chip${surSynthese ? ' active' : ''}`} onClick={() => setEtape(n)}>
          Synthèse
        </button>
        <span style={{ flex: 1 }} />
        <button className="btn btn-outline btn-sm" onClick={() => setPapier(true)}>Imprimer la grille</button>
      </div>

      {section && (
        <>
          {section.consigne && <div className="ent-consigne">{section.consigne}</div>}
          {section.questions.map((q) => {
            const v = reponses[q.id]
            return (
              <div className="ent-q" key={q.id}>
                <div className="t">
                  {q.texte}
                  {q.eliminatoire && <span className="badge badge-red" style={{ marginLeft: 8 }}>Éliminatoire</span>}
                </div>
                {q.aide && <div className="aide">{q.aide}</div>}
                {q.type === 'note' && (
                  <div className="ent-notes" role="group" aria-label="Note de 1 à 5">
                    {[1, 2, 3, 4, 5].map((k) => (
                      <button key={k} className={v === k ? 'on' : ''} onClick={() => repondre(q.id, k)}>{k}</button>
                    ))}
                  </div>
                )}
                {q.type === 'oui_non' && (
                  <div className="ent-notes" role="group" aria-label="Oui ou non">
                    <button className={`large${v === true ? ' on' : ''}`} onClick={() => repondre(q.id, true)}>Oui</button>
                    <button className={`large${v === false ? ' on non' : ''}`} onClick={() => repondre(q.id, false)}>Non</button>
                  </div>
                )}
                {q.type === 'texte' && (
                  <textarea
                    style={{ marginTop: 8 }}
                    rows={3}
                    value={typeof v === 'string' ? v : ''}
                    onChange={(e) => repondre(q.id, e.target.value)}
                  />
                )}
              </div>
            )
          })}
        </>
      )}

      {surSynthese && (
        <>
          <div className="ent-consigne">Notez chaque critère de 1 à 5, choisissez la recommandation finale, puis enregistrez.</div>
          {canevas.synthese.criteres.map((k) => (
            <div className="ent-q" key={k.id}>
              <div className="t">{k.libelle}</div>
              <div className="ent-notes" role="group" aria-label={`Note : ${k.libelle}`}>
                {[1, 2, 3, 4, 5].map((x) => (
                  <button
                    key={x}
                    className={criteres[k.id] === x ? 'on' : ''}
                    onClick={() => setCriteres((c) => ({ ...c, [k.id]: x }))}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="ent-q">
            <div className="t">Recommandation finale</div>
            <div className="aide">
              Suggestion : <b>{LIBELLE_RECO[suggeree]}</b>. Vous pouvez la changer
              {elimine ? ', sauf « retenir » (critère éliminatoire déclenché).' : '.'}
            </div>
            <div className="ent-notes" role="group" aria-label="Recommandation">
              {(['retenir', 'revoir', 'refuser'] as const).map((r) => (
                <button
                  key={r}
                  className={`large${reco === r ? ' on' : ''}${reco === r && r === 'refuser' ? ' non' : ''}`}
                  disabled={!recommandationAutorisee(r, elimine)}
                  onClick={() => setChoix(r)}
                >
                  {LIBELLE_RECO[r]}
                </button>
              ))}
            </div>
          </div>
          <div className="ent-q">
            <div className="t">Notes libres</div>
            <textarea style={{ marginTop: 8 }} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </>
      )}

      <div className="ent-resume">
        <div>
          Score calculé : <span className="score">{score.toFixed(2).replace('.', ',')}</span> / 5
          <span className="dos-note"> (moyenne des notes renseignées)</span>
        </div>
        {elimine ? (
          <div className="dos-erreur">
            Critère éliminatoire déclenché : {declenchees.map((q) => `« ${q.texte} »`).join(' ; ')}
          </div>
        ) : (
          <div className="dos-note">Aucun critère éliminatoire déclenché.</div>
        )}
      </div>

      <Erreur texte={erreur} />
      <div className="ent-pied">
        <button className="btn btn-outline btn-sm" disabled={etape === 0} onClick={() => setEtape((e) => Math.max(0, e - 1))}>
          Précédent
        </button>
        {!surSynthese ? (
          <button className="btn btn-accent btn-sm" onClick={() => setEtape((e) => Math.min(n, e + 1))}>
            {etape === n - 1 ? 'Passer à la synthèse' : 'Suivant'}
          </button>
        ) : (
          <button className="btn btn-accent btn-sm" disabled={occupe} onClick={() => void enregistrer()}>
            {occupe ? 'Enregistrement…' : `Enregistrer l'entretien (${LIBELLE_RECO[reco].toLowerCase()})`}
          </button>
        )}
      </div>

      {papier && <GrillePapier canevas={canevas} candidat={nom} surFermer={() => setPapier(false)} />}
    </>
  )
}
